import type { CSSProperties, ReactElement } from 'react';
import type { ConversationSummary } from '@zenda/api-contract';
import { ClientTag } from './ClientTag';
import { Icon, type IconName } from './Icon';
import { useTip } from './Tip';
import type { Dictionary } from '../content/pt-BR';
import { useAuth } from '../lib/auth';
import { useT } from '../lib/i18n';
import {
  formatCountdown,
  formatDateFull,
  formatStamp,
  formatWaiting,
  initials,
} from '../lib/format';
import { janelaApertando, nivelDeEspera } from '../lib/urgencia';
import { pilotoNoAvatar } from './PilotoNaLinha';
import '../styles/inbox.css';

/**
 * O estado da conversa, num selo no canto do avatar.
 *
 * Um selo só, sempre no mesmo canto: o olho aprende o lugar e lê o símbolo. A conversa em
 * dia não tem selo, e a ausência também significa. Só "precisa de resposta" leva a cor de
 * ação, porque é a única das três que pede que alguém faça algo agora.
 */
function seloDeEstado(
  t: Dictionary,
  c: ConversationSummary,
): { readonly icon: IconName | null; readonly tipo: string; readonly titulo: string } | null {
  if (c.status === 'resolved')
    return { icon: 'check', tipo: 'feita', titulo: t.inbox.hint.resolved };
  if (!c.serviceWindow.open) return { icon: 'lock', tipo: 'fechada', titulo: t.inbox.hint.locked };
  if (c.awaitingReply) return { icon: null, tipo: 'responder', titulo: t.inbox.hint.needsReply };
  return null;
}

/** A mesma pessoa tem a mesma cor em toda linha, sem precisar guardar cor de ninguém. */
const TONS = ['azul', 'menta', 'ambar', 'rosa', 'lilas'] as const;

function tomDe(id: string): (typeof TONS)[number] {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TONS[Math.abs(h) % TONS.length]!;
}

/**
 * Quem está com a conversa, no canto da linha. Sem ninguém, nada: o filtro "Sem ninguém"
 * já responde a essa pergunta, e um selo vazio em toda linha seria ruído.
 */
export function Responsavel({
  id,
  nome,
  eu,
}: {
  readonly id: string;
  readonly nome: string;
  readonly eu: boolean;
}): ReactElement {
  const t = useT();
  const tip = useTip();
  const primeiro = nome.trim().split(/\s+/)[0] ?? nome;
  return (
    <span
      className={`conv__resp${eu ? ' conv__resp--eu' : ` conv__resp--${tomDe(id)}`}`}
      {...tip(eu ? t.inbox.hint.comVoce : t.inbox.hint.comAlguem(nome))}
    >
      <span className="conv__resp-av" aria-hidden="true">
        {initials(nome)}
      </span>
      {eu ? t.inbox.voce : primeiro}
    </span>
  );
}

/**
 * Uma conversa na lista do inbox.
 *
 * Vive num arquivo próprio porque são cem linhas de decisão visual densa — glifo de
 * status, um único sinal de tempo, prévia, não-lidas e etiquetas — que dentro do
 * `map()` da página escondiam a estrutura das três colunas.
 */
export function LinhaDeConversa({
  conversa: c,
  ativa,
  onSelecionar,
}: {
  readonly conversa: ConversationSummary;
  readonly ativa: boolean;
  readonly onSelecionar: () => void;
}): ReactElement {
  const t = useT();
  const tip = useTip();
  const eu = useAuth().me?.userId ?? null;
  const w = c.serviceWindow;
  const fechando = janelaApertando(w);
  const espera = nivelDeEspera(c.minutesWaiting);
  const selo = seloDeEstado(t, c);
  const av = pilotoNoAvatar(t, c);

  return (
    <button
      type="button"
      onClick={onSelecionar}
      className={`conv${ativa ? ' is-active' : ''}${c.status === 'resolved' ? ' is-resolved' : ''}`}
    >
      <span className={`conv__av${av.classe}`} {...(av.dica === null ? {} : tip(av.dica))}>
        <span aria-hidden="true">{av.conteudo}</span>
        {av.dica === null ? null : <span className="sr-only">{av.dica}</span>}
        {selo === null ? null : (
          <span className={`conv__selo conv__selo--${selo.tipo}`} {...tip(selo.titulo)}>
            {selo.icon === null ? null : <Icon name={selo.icon} size={9} />}
          </span>
        )}
      </span>
      <div className="conv__main">
        <div className="conv__top">
          <span className="conv__name">{c.contactName}</span>
          <span
            className="conv__time num"
            {...tip(
              c.lastMessageAt === null ? '' : t.inbox.hint.stamp(formatDateFull(c.lastMessageAt)),
            )}
          >
            {c.lastMessageAt === null ? '' : formatStamp(c.lastMessageAt)}
          </span>
        </div>

        {/* UM sinal de tempo, nunca dois.
            "esperando ha 1 h" e "fecha em 38 min" respondem a mesma pergunta — quao
            urgente e isto — e mostrar os dois lado a lado tingia cada linha duas vezes
            sem dizer nada a mais. A ordem e a da consequencia: a espera longa e a que
            vira reclamacao com o medico, e por isso vem antes da janela que ainda vai
            fechar. Janela FECHADA nao entra aqui: quem a diz e o cadeado no slot de
            status, porque ela muda o que se PODE fazer. */}
        <div className="conv__bottom">
          <span className="conv__preview">{c.lastMessagePreview ?? t.inbox.noMessages}</span>
          {/* A conversa ABERTA nunca mostra badge, mesmo que a lista ainda traga o
              contador antigo. Marcar como lida e uma ida ao servidor; ate a lista voltar,
              o numero contradiz as mensagens que estao a vista. Aqui a tela ganha do
              cache. */}
          {c.unreadCount > 0 && !ativa ? (
            <span className="conv__unread num" {...tip(t.inbox.hint.unread(c.unreadCount))}>
              {c.unreadCount}
            </span>
          ) : null}
        </div>

        <div className="conv__meta">
          <ClientTag name={c.clientName} color={c.clientColor} variant="plain" />
          {c.ownerName === null ? null : (
            <span className="conv__dona">{t.inbox.da(c.ownerName)}</span>
          )}
          {espera === 'late' || espera === 'warn' ? (
            <span
              className={`conv__flag conv__flag--wait-${espera}`}
              {...tip(t.inbox.hint.waiting(formatWaiting(c.minutesWaiting ?? 0)))}
            >
              <Icon name="clock" size={11} />
              {formatWaiting(c.minutesWaiting ?? 0)}
            </span>
          ) : fechando ? (
            <span
              className="conv__flag conv__flag--soon"
              {...tip(t.inbox.hint.closingIn(formatCountdown(w.minutesRemaining ?? 0)))}
            >
              {t.inbox.windowClosingIn(formatCountdown(w.minutesRemaining ?? 0))}
            </span>
          ) : null}
          {c.assigneeId === null || c.assigneeName === null ? null : (
            <Responsavel id={c.assigneeId} nome={c.assigneeName} eu={c.assigneeId === eu} />
          )}
        </div>

        {/* Duas tags e o resto vira contagem. Uma conversa com cinco etiquetas empurrava
            o texto da mensagem para fora da linha — e e o texto que diz do que se trata. */}
        {c.tags.length === 0 ? null : (
          <div className="conv__tags">
            {c.tags.slice(0, 2).map((tag) => (
              <span
                className="tagchip tagchip--quiet"
                key={tag.id}
                {...tip(t.inbox.hint.tag(tag.name))}
                style={{ '--tag': tag.color } as CSSProperties}
              >
                <span className="tagchip__dot" aria-hidden="true" />
                {tag.name}
              </span>
            ))}
            {c.tags.length > 2 ? (
              <span className="conv__tags-more" {...tip(t.inbox.hint.moreTags(c.tags.length - 2))}>
                +{c.tags.length - 2}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </button>
  );
}
